import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useHome } from "@/hooks/useMovies";
import { HeroMovie } from "@/components/hero/HeroMovie";
import { MovieRow } from "@/components/movie/MovieRow";
import { HeroSkeleton, RowSkeleton } from "@/components/skeleton/Skeletons";
import { EmptyState, ErrorState } from "@/components/common/States";
import { Page } from "@/components/common/Reveal";
import { GlassButton } from "@/components/glass/Glass";
import { IconPlus } from "@/components/icons/Icons";
import { useAuth } from "@/context/AuthContext";
import { progressService } from "@/services/userService";
import { useSeo } from "@/lib/seo";
import { AnimatePresence, motion } from "framer-motion";
import { AdSlot } from "@/components/common/Ads";

function ContinueWatching() {
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ["continue", user?.user_id], queryFn: progressService.continueWatching, enabled: Boolean(user), staleTime: 30 * 1000 });
  if (!data?.length) return null;
  return <MovieRow title="Davom ettirish" items={data} showProgress testId="row-continue" eager />;
}

export default function Home() {
  const { data, isLoading, isError, error, refetch } = useHome();
  const { isAdmin } = useAuth();
  useSeo({});

  return (
    <Page className="pk-pb-dock">
      <AnimatePresence mode="wait" initial={false}>
        {isLoading ? (
          <motion.div key="sk" exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
            <HeroSkeleton />
            <div className="-mt-20 relative">
              <RowSkeleton />
              <RowSkeleton />
            </div>
          </motion.div>
        ) : isError ? (
          <motion.div key="err" className="pt-32">
            <ErrorState error={error} onRetry={refetch} />
          </motion.div>
        ) : !data.total ? (
          <motion.div key="empty" className="pt-32">
            <EmptyState
              title="Katalog hozircha bo‘sh"
              text="Tez orada bu yerda eng yaxshi o‘zbek filmlari paydo bo‘ladi."
              testId="home-empty-state"
              action={
                isAdmin && (
                  <Link to="/admin/new">
                    <GlassButton variant="primary" data-testid="home-add-movie-button">
                      <IconPlus size={18} /> Film qo‘shish
                    </GlassButton>
                  </Link>
                )
              }
            />
          </motion.div>
        ) : (
          <motion.div key="content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
            <HeroMovie movies={data.hero} />
            <div className="relative z-[2] -mt-10 sm:-mt-16">
              <ContinueWatching />
              {data.rows.map((r, i) => (
                <div key={r.key}>
                  <MovieRow title={r.title} items={r.items} href={r.href || (r.key === "trending" ? "/movies" : undefined)} testId={`row-${r.key}`} eager={i < 2} />
                  {i === 1 && <AdSlot name="banner_home" />}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Page>
  );
}
